<template>
  <CardFlat
    id="price-trends"
    hasInfoIcon
    :iconPosition="isIS24 ? 'right' : 'static'"
    @onInfoIconClick="isModalVisible = true"
  >
    <template #title>{{ t('priceTrends.heading') }}</template>
    <template #description>
      {{
        t('priceTrends.caption.' + propertyType, [
          getLocationSentence(t, locale as HgSupportedLanguage, { includePreposition: false }),
        ])
      }}
    </template>
    <PriceTrendsChart />
    <HgModal
      id="price-trends-modal"
      v-model="isModalVisible"
      mode="dialog"
      @close="isModalVisible = false"
    >
      <template #title> {{ t('priceTrends.heading') }}</template>
      <div
        v-for="{ title, description } in modalContent"
        :key="title"
        :class="$style.tooltipContentWrapper"
      >
        <p :class="$style.tooltipContentHeading">{{ title }}</p>
        <p :class="$style.tooltipContentDescription" v-html="description" />
      </div>
    </HgModal>
  </CardFlat>
</template>
<script setup lang="ts">
import { HgModal, type HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { CardFlat } from '@/components/common';
import { isPlatformIs24 } from '@/helpers/platform';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';

const isIS24 = isPlatformIs24();

const isModalVisible = ref<boolean>(false);
const mapStore = useMapStore();
const { propertyType } = storeToRefs(mapStore);
const { getLocationSentence } = storeToRefs(useGeoStore());
const { t, locale } = useI18n();

const PriceTrendsChart = defineAsyncComponent(() =>
  isIS24
    ? import('@/components/price-trends/PriceTrendsChartIs24.vue')
    : import('@/components/price-trends/PriceTrendsChartHg.vue'),
);

const modalContent = computed<Array<{ title: string; description: string }>>(() => [
  {
    title: t('tooltips.dataUpdatePriceTrendsTitle'),
    description: t('tooltips.dataUpdatePriceTrends'),
  },
  {
    title: t('tooltips.marketVsOfferedTitle'),
    description: t('tooltips.marketVsOffered'),
  },
  {
    title: t('tooltips.medianPriceTitle'),
    description: t('tooltips.medianPrice'),
  },
  {
    title: t('tooltips.hedonicValuationTitle'),
    description: t('tooltips.hedonicValuation'),
  },
]);
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.noDataContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100% !important;
  height: 280px !important;
}

.tooltipContentWrapper {
  margin-bottom: $space-250;
}

.tooltipContentDescription {
  margin-top: 0;
  margin-bottom: 0;

  @include body-2;
}

.tooltipContentHeading {
  margin-top: 0;
  margin-bottom: 0;

  @include heading-5;
}
</style>
